import React, { createContext, useContext, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useCookies } from 'react-cookie';
import PropTypes from "prop-types";
import { useLayers, useSettings } from '@context';
import { BasemapList } from "@utils";
import { useColorScheme } from '@mui/joy/styles';
import { maxeleImperialStyle, maxwvelImperialKnotsStyle, swanImperialStyle } from '@utils';

// create a new context
const AuthContext = createContext();


// guest login defination
export const GuestAccount= {
    "success": true,
    "role": {"type": "User"},
    "profile": {
        "email": "Guest",
        "role_id": 0,
        "details": {"useUTC": "false", "basemap": "USGS Topo", "darkMode": "light",
            "last_name": "Guest", "speedType": "knots", "unitsType": "imperial", "created_on": "",
            "first_name": "Guest", "swan_opacity": "1", "maxele_opacity": "1", "maxwvel_opacity": "1",
            "external_opacity": "1", "animate_opacity": "1"},
        "maxelestyle": null,
        "maxwvelstyle": null,
        "swanstyle": null
    }
};

/**
 * component that provides the authentication of the user
 *
 * @param children
 * @returns React.ReactElement
 * @constructor
 */
export const AuthProvider = ({ children }) => {
    // assign local storage for the user profile
    const [userProfile, setUserProfile, removeUserProfile] = useCookies(['userProfile']);
    const navigate = useNavigate();
    const { setBaseMap } = useLayers();

    // get all the associated setting handles
    const { useUTC, unitsType, mapStyle, layerOpacity, speedType, setChangesMade} = useSettings();
    const { mode, setMode } = useColorScheme();

    // call this function to authenticate the user
    const login = async (userProfile) => {
        // if there was a user profile returned
        if (userProfile) {
            // apply the settings from the user profile
            applyUserSettings(userProfile);

            // remove the layer styles in the profile
            delete userProfile.profile.maxelestyle;
            delete userProfile.profile.maxwvelstyle;
            delete userProfile.profile.swanstyle;

            // save the user profile data
            setUserProfile('userProfile', userProfile, { path: '/', maxAge: 21600 });

            // reset the settings changed indicator
            setChangesMade(false);

            // redirect to the main page
            navigate('/');
        }
    };

    // call this function to sign out a logged-in user
    const logout = () => {
        // remove the user data
        removeUserProfile('userProfile');

        // redirect to the login page
        navigate('/login');
    };

    // call this to navigate to the add-user page
    const addUser = () => {
        // remove the user data
        removeUserProfile('userProfile');

        // redirect to the add a user page
        navigate('/login', { replace: true });
    };

    // call this to navigate to the add-user page
    const navAddUser = () => {
        // remove the user data
        removeUserProfile('userProfile');

        // redirect to the add a user page
        navigate('/add-user', { replace: true });
    };

    // call this to navigate to the update user page
    const updateUser = (newUserProfile) => {
        // remove the user data
        setUserProfile('userProfile', newUserProfile, { path: '/', maxAge: 21600 });
    };

    /**
     * loads app settings with the user's selections
     *
     * @param userProfile
     */
    const applyUserSettings = (userProfile) => {
        // apply all settings found in the user's profile details
        if (userProfile !== undefined) {
            try {
                // load the layer styles
                loadSelectedLayerStyles(userProfile.profile);

                // load the profile details
                loadProfileDetails(userProfile.profile.details);
            } catch (e) {
                // corrupt or malformed profile - clear the cookie and redirect to login
                console.error('Corrupt user profile detected, clearing session.', e);
                removeUserProfile('userProfile');
                navigate('/login');
            }
        }
    };

    /**
     * load the layer styles
     *
     * @param userProfile
     */
    const loadSelectedLayerStyles = (profile) => {
        // set the maxele style from the DB ot use the default
        if (profile.maxelestyle)
            mapStyle.maxele.set(profile.maxelestyle);
        else
            mapStyle.maxele.set(maxeleImperialStyle);

        // set the maxwvel style from the DB ot use the default
        if (profile.maxwvelstyle)
            mapStyle.maxwvel.set(profile.maxwvelstyle);
        else
            mapStyle.maxwvel.set(maxwvelImperialKnotsStyle);

        // set the swan style from the DB ot use the default
        if (profile.swanstyle)
            mapStyle.swan.set(profile.swanstyle);
        else
            mapStyle.swan.set(swanImperialStyle);
    };

    /**
     * loads the profile details from the DB
     *
     * @param userProfile
     */
    const loadProfileDetails = (profile) => {
        // use the base map if it was specified
        if (profile.basemap) {
            // get the basemap entry from the list
            const selectedBasemap = BasemapList.filter((map) => map.title === profile.basemap);

            // if the entry was found
            if (selectedBasemap.length === 1) {
                // set the base map in state
                setBaseMap(selectedBasemap[0]);

                // set the basemap in local storage
                localStorage.setItem('basemap', JSON.stringify(selectedBasemap[0].title));
            }
        }

        // if the mode is different from what is currently set
        if (mode !== profile.darkMode) {
            // set the new dark or light mode
            setMode(profile.darkMode === "dark" ? 'dark' : 'light');
        }

        // set the other user settings when they exist in the DB
        if (profile.useUTC) profile.useUTC === 'true' ? useUTC.set() : useUTC.unset();
        if (profile.unitsType) unitsType.set(profile.unitsType);
        if (profile.speedType) speedType.set(profile.speedType);
        if (profile.maxwvel_opacity) layerOpacity.maxwvel.set(parseFloat(profile.maxwvel_opacity));
        if (profile.maxele_opacity) layerOpacity.maxele.set(parseFloat(profile.maxele_opacity));
        if (profile.swan_opacity) layerOpacity.swan.set(parseFloat(profile.swan_opacity));
        if (profile.external_opacity) layerOpacity.external.set(parseFloat(profile.external_opacity));
        if (profile.animate_opacity) layerOpacity.animate.set(parseFloat(profile.animate_opacity));
    };

    /**
     * define the authentication methods and user profile
     *
     */
    const value = useMemo(
        () => ({
            userProfile,
            login,
            logout,
            navAddUser,
            addUser,
            updateUser
        }), [userProfile]
    );

    // return a new authentication provider
    return <AuthContext.Provider value={ value }>{ children }</AuthContext.Provider>;
};

// define the object properties
AuthProvider.propTypes = {
    children: PropTypes.node
};

/**
 * creates an authorization component
 *
 * @returns {*}
 */
export const userAuth = () => {
    return useContext(AuthContext);
};
